import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Panel } from 'primereact/panel';
import { Toast } from 'primereact/toast';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useSite } from '../../context/site';
import { NAME } from '../../shared';
import ListItemCreate from './create';
import ListItemUpdate from './update';

function ListItemsTable({ type }) {
    const { lists } = useSite();

    const [listsLoaded, setListsLoaded] = useState(false);
    const [listsByType, setListsByType] = useState([]);
    const [defaultList, setDefaultList] = useState('');

    const [globalFilterValue, setGlobalFilterValue] = useState('');

    const [openCreate, setOpenCreate] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [updateData, setUpdateData] = useState({ ...DEFAULT_ITEM_DATA });

    const toast = useRef(null);

    const rowClass = (data) => {
        return {
            rowInactive: !data.active
        };
    };

    const onGlobalFilterChange = useCallback(
        (e) => {
            const needle = e.target.value.toLowerCase();

            setGlobalFilterValue(needle);

            if (!needle) {
                setListsByType([...lists.filter((haystack) => haystack.listType === type)]);
                return;
            }

            setListsByType([
                ...lists.filter(
                    (haystack) =>
                        haystack.listType === type &&
                        haystack.itemName.trim().toLowerCase().startsWith(needle)
                )
            ]);
        },
        [type, lists]
    );

    const renderHeader = () => {
        return (
            <div className="grid">
                <div className="col">
                    <Button
                        icon="pi pi-plus"
                        rounded
                        severity="success"
                        onClick={() => {
                            setOpenCreate(true);
                        }}
                    />
                </div>

                <div className="col">
                    Default Item: <Chip label={defaultList} />
                </div>

                <div className="col">
                    <div className="flex justify-content-end">
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText
                                value={globalFilterValue}
                                onChange={onGlobalFilterChange}
                                placeholder="Name Search"
                            />
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    // Set List Items
    useEffect(() => {
        if (Array.isArray(lists)) {
            setListsLoaded(false);

            const _lists = lists.filter((l) => l.listType === type);

            setListsByType(_lists);
            setListsLoaded(true);
            setDefaultList(_lists.find((l) => l.itemIsDefault).itemName);
        }
    }, [type, lists]);

    const header = renderHeader();

    return (
        <Panel className="mt-3">
            <DataTable
                value={listsByType}
                showGridlines
                removableSort
                paginator
                dataKey="_id"
                header={header}
                emptyMessage={`${type} - No items found`}
                rows={5}
                rowsPerPageOptions={[5, 10, 25, 50]}
                loading={!listsLoaded}
                rowClassName={rowClass}
                onRowClick={(e) => {
                    setUpdateData({ ...e.data });
                    setOpenUpdate(true);
                }}
            >
                <Column field="itemName" header="Name" sortable></Column>

                {type === NAME.listType.state ? (
                    <Column field="itemData.abbr" header="Abbr" sortable></Column>
                ) : null}

                <Column field="active" header="Active" sortable></Column>
                <Column field="itemIsDefault" header="Default" sortable></Column>
            </DataTable>

            {!openCreate ? null : (
                <ListItemCreate
                    open={openCreate}
                    type={type}
                    onClose={(res) => {
                        if (res.status === 200) {
                            toast.current.show({
                                severity: 'success',
                                summary: 'Success',
                                detail: `Successfully Created ${res.data.list.itemName}`,
                                life: 3000
                            });
                        }

                        setOpenCreate(false);
                    }}
                />
            )}

            {!openUpdate ? null : (
                <ListItemUpdate
                    open={openUpdate}
                    data={updateData}
                    type={type}
                    onClose={(res) => {
                        if (res.status === 200) {
                            toast.current.show({
                                severity: 'success',
                                summary: 'Success',
                                detail: `Successfully Updated ${res.data.list.itemName}`,
                                life: 3000
                            });
                        }

                        setOpenUpdate(false);
                    }}
                />
            )}

            <Toast ref={toast} />
        </Panel>
    );
}

const DEFAULT_ITEM_DATA = Object.freeze({
    _id: '',
    active: false,
    listType: '',
    itemName: '',
    itemIsDefault: false,
    itemData: {}
});

export default ListItemsTable;
